import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { 
  Server, 
  Wifi, 
  Copy, 
  Download,
  Smartphone,
  Monitor,
  Globe,
  Zap,
  Users,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { usersApi } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { toast } from 'react-hot-toast'

interface UnifiedNode {
  id: string
  name: string
  host: string
  port: number
  protocol: string
  country: string
  city: string
  flag_emoji: string
  node_type: 'traditional' | 'edgetunnel'
  group_id?: number
  group_name?: string
  user_level?: number
  sort_order: number
}

interface SubscriptionData {
  subscription: any
  servers: UnifiedNode[]
  edgetunnel_nodes: UnifiedNode[]
  all_nodes: UnifiedNode[]
  node_stats: {
    traditional_count: number
    edgetunnel_count: number
    total_count: number
  }
  links: {
    clash: string
    v2ray: string
    shadowrocket: string
  }
}

export default function ServersPage() {
  const [activeTab, setActiveTab] = useState('all')

  const { data: subscriptionData, isLoading, error } = useQuery({
    queryKey: ['subscription-links'],
    queryFn: async () => {
      const response = await usersApi.getSubscriptionLinks()
      return response.data.data as SubscriptionData
    },
  })

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${label} 已复制到剪贴板`)
    } catch (err) {
      toast.error('复制失败，请手动复制')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !subscriptionData) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">节点列表</h1>
          <p className="text-gray-600">查看可用的服务器节点</p>
        </div>

        <Card>
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              无法获取节点信息
            </h3>
            <p className="text-gray-600 mb-6">
              请确保您有有效的订阅，然后重试
            </p>
            <Button asChild>
              <a href="/user/subscription">查看订阅</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { all_nodes, servers, edgetunnel_nodes, node_stats, links } = subscriptionData

  const renderNodeCard = (node: UnifiedNode) => (
    <Card key={node.id} className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">{node.flag_emoji}</div>
            <div>
              <h3 className="font-medium text-gray-900">{node.name}</h3>
              <p className="text-sm text-gray-600">{node.country} - {node.city}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Badge 
              variant={node.node_type === 'traditional' ? 'default' : 'secondary'}
            >
              {node.node_type === 'traditional' ? '传统节点' : 'EdgeTunnel'}
            </Badge>
            <Badge variant="outline">
              {node.protocol.toUpperCase()}
            </Badge>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">地址</span>
            <span className="font-mono text-gray-900">{node.host}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">端口</span>
            <span className="font-mono text-gray-900">{node.port}</span>
          </div>
          {node.node_type === 'edgetunnel' && node.user_level && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">用户等级</span>
              <Badge variant="outline">Level {node.user_level}</Badge>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-1 text-sm text-gray-600">
            <Wifi className="w-4 h-4" />
            <span>在线</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => copyToClipboard(`${node.host}:${node.port}`, '节点地址')}
          >
            <Copy className="w-4 h-4 mr-1" />
            复制
          </Button>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">节点列表</h1>
        <p className="text-gray-600">查看所有可用的服务器节点</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">总节点数</p>
                <p className="text-xl font-bold text-gray-900">{node_stats.total_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Server className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">传统节点</p>
                <p className="text-xl font-bold text-gray-900">{node_stats.traditional_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Zap className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">EdgeTunnel</p>
                <p className="text-xl font-bold text-gray-900">{node_stats.edgetunnel_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Users className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">在线节点</p>
                <p className="text-xl font-bold text-gray-900">{node_stats.total_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscription Links */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Download className="w-5 h-5 mr-2" />
            订阅链接
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Smartphone className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium">Clash</span>
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={links.clash}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 font-mono"
                />
                <Button
                  size="sm"
                  onClick={() => copyToClipboard(links.clash, 'Clash 订阅链接')}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Monitor className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium">V2Ray</span>
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={links.v2ray}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 font-mono"
                />
                <Button
                  size="sm"
                  onClick={() => copyToClipboard(links.v2ray, 'V2Ray 订阅链接')}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Smartphone className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium">Shadowrocket</span>
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={links.shadowrocket}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 font-mono"
                />
                <Button
                  size="sm"
                  onClick={() => copyToClipboard(links.shadowrocket, 'Shadowrocket 订阅链接')}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Node Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="all">
            全部节点 ({node_stats.total_count})
          </TabsTrigger>
          <TabsTrigger value="traditional">
            传统节点 ({node_stats.traditional_count})
          </TabsTrigger>
          <TabsTrigger value="edgetunnel">
            EdgeTunnel ({node_stats.edgetunnel_count})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {all_nodes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {all_nodes.map(renderNodeCard)}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Server className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">暂无可用节点</h3>
                <p className="text-gray-600">请联系管理员添加节点</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="traditional" className="space-y-4">
          {servers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {servers.map(renderNodeCard)}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Server className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">暂无传统节点</h3>
                <p className="text-gray-600">管理员尚未配置传统服务器节点</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="edgetunnel" className="space-y-4">
          {edgetunnel_nodes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {edgetunnel_nodes.map(renderNodeCard)}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">暂无 EdgeTunnel 节点</h3>
                <p className="text-gray-600">管理员尚未配置 EdgeTunnel 节点</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}